import { formatSecondsAgo } from "../../../../app/dateHelpers";
import { SpinnerDefault } from "../../../../components";
import { GaugeConfig } from "../../../../data/entities";
import { formatPeakTimestamp } from "../../helper";
import { LiveGauge } from "../upload-sessions";

export interface FlwGaugeData {
  config: GaugeConfig;
  isReady: boolean;
  currentDifference: number | null;
  peakDifference: number | null;
  peakDifferenceAt: string | null;
  windowSize: number | null;
}

interface GaugeCardProps {
  currentFlawValue: number;
  peakFlawValue: number | null;
  peakFlawValueAt: string | null;
  config: GaugeConfig | null;
  flw: FlwGaugeData | null;
  isLive: boolean;
  lastDataTimestamp: number | null;
  secondsAgo: number;
}

function GaugeSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="text-muted fw-semibold text-uppercase text-center mb-1"
      style={{ fontSize: "0.65rem", letterSpacing: "0.04em" }}
    >
      {children}
    </div>
  );
}

function PeakTimestamp({ label, isoString }: { label: string; isoString: string }) {
  const ts = formatPeakTimestamp(isoString);
  return (
    <div className="text-center mt-3" style={{ fontSize: "0.75rem" }}>
      <div
        className="text-muted fw-semibold text-uppercase"
        style={{ fontSize: "0.65rem", letterSpacing: "0.04em" }}
      >
        {label}
      </div>
      <div className="text-muted">{ts.local}</div>
    </div>
  );
}

export function GaugeCard({
  currentFlawValue,
  peakFlawValue,
  peakFlawValueAt,
  config,
  flw,
  isLive,
  lastDataTimestamp,
  secondsAgo,
}: GaugeCardProps) {
  const hasFlw = flw != null;

  return (
    <div className="d-flex flex-column align-items-center w-100">
      <div className="d-flex flex-wrap justify-content-center align-items-start w-100 gap-3">
        {/* Flaw gauge */}
        <div className="w-100 flex-shrink-0" style={{ maxWidth: 300 }}>
          {hasFlw && <GaugeSectionLabel>Flaw Signal</GaugeSectionLabel>}
          {config ? (
            <LiveGauge
              currentFlawValue={currentFlawValue}
              peakFlawValue={peakFlawValue}
              config={config}
            />
          ) : (
            <div className="text-center text-muted py-4">
              <SpinnerDefault />
              <small className="d-block mt-2">Loading gauge settings&hellip;</small>
            </div>
          )}
          {peakFlawValueAt && <PeakTimestamp label="Peak Flaw At" isoString={peakFlawValueAt} />}
        </div>

        {/* FLW rolling-average deviation gauge */}
        {hasFlw && (
          <div className="w-100 flex-shrink-0" style={{ maxWidth: 300 }}>
            <GaugeSectionLabel>Rolling Avg Deviation</GaugeSectionLabel>
            {flw.isReady ? (
              <>
                <LiveGauge
                  currentFlawValue={flw.currentDifference}
                  peakFlawValue={flw.peakDifference}
                  config={flw.config}
                />
                {flw.peakDifferenceAt && (
                  <PeakTimestamp label="Peak Deviation At" isoString={flw.peakDifferenceAt} />
                )}
              </>
            ) : (
              <div className="text-center text-muted py-4">
                <small className="d-block mb-2">
                 Collecting more data
                </small>
                <div className="progress mx-auto" style={{ height: 4, maxWidth: 180 }}>
                  <div
                    className="progress-bar bg-secondary progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: "100%" }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Freshness indicator */}
      {isLive && lastDataTimestamp != null && (
        <div className="mt-3">
          <small className="d-flex align-items-center gap-1 text-muted">
            <span
              className="d-inline-block rounded-circle bg-success"
              style={{ width: 6, height: 6 }}
            />
            Last data received: {formatSecondsAgo(secondsAgo)}
          </small>
        </div>
      )}
    </div>
  );
}
